import { Spacing } from '@persona-id/design-tokens';
import { TFunction } from 'i18next';
import { throttle } from 'lodash/fp';
import { MouseEventHandler, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import Button from 'inquiry/components/Button';
import {
  InvalidSelfieReason,
  PoseState,
} from 'inquiry/scanners/ConfigurablePosesScanner/PoseStateManager';
import useIsMounted from 'lib/hooks/useIsMounted';
import usePrevious from 'lib/hooks/usePrevious';
import { colorCss, StyleValueColorConfig } from 'lib/inquiryStyles';
import { SerializedStepSelfieStyles, StepSelfieConfig } from 'lib/interfaces/StepsConfig';
import { ButtonType } from 'lib/interfaces/ui-components';
import { isIOS17Phone } from 'lib/utils/device';
import { isInCypress } from 'lib/utils/dom';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import { SelfieStep as SelfiePose, SelfieStep } from 'verify/lib/interfaces/selfie';
import { BaseHintPrompt } from 'verify/scanners/SelfieScanner/HintPrompt';
import ProgressOverlay from 'verify/scanners/SelfieScanner/ProgressOverlay';

const HINT_DELAY = 300; // number of ms to delay hints by so people have enough time to read them

interface ConfigurablePosesScannerViewProps {
  assets?: StepSelfieConfig['assets']['record-page'];
  children?: React.ReactNode;
  delayUntilManualCaptureMs?: number | null;
  enableBlur?: boolean;
  localizations?: StepSelfieConfig['localizations']['capture-page'];
  poseOrder: SelfiePose[];
  poseState: PoseState | null;
  profileSize: number;
  showLoadingSpinner: boolean;
  styles?: SerializedStepSelfieStyles;
  videoHeight: number | null;
  handleManualCapture: MouseEventHandler<HTMLButtonElement>;
  enableMobilePendingExperienceForWeb: boolean;
}

type HintType = SelfiePose | InvalidSelfieReason | 'done';

export const ConfigurablePosesScannerView = ({
  assets,
  children,
  delayUntilManualCaptureMs,
  localizations,
  poseOrder,
  poseState,
  profileSize,
  showLoadingSpinner,
  styles,
  videoHeight,
  handleManualCapture,
  enableMobilePendingExperienceForWeb,
}: ConfigurablePosesScannerViewProps) => {
  const [hasFace, setHasFace] = useState<boolean>(false);
  const [manualButtonVisible, setManualButtonVisible] = useState<boolean>(
    delayUntilManualCaptureMs === 0 || isInCypress() ? true : false,
  );
  // Set the very first hint to always be center pose, since that is always a requirement
  const [hintType, setHintType] = useState<HintType>(SelfiePose.Center);
  const isMounted = useIsMounted();
  const previousPoseState = usePrevious(poseState);

  const throttleSetHintType = useCallback(
    (h: HintType) => {
      throttle(HINT_DELAY, (innerHint: HintType) => {
        if (!isMounted) return;
        setHintType(innerHint);
      })(h);
    },
    [setHintType, isMounted],
  );

  const [t] = useTranslation();

  /**
   * This effect is essentially the same as a tick -- it's run every time poseState changes
   * which maps to the `runPoseDetection` timeout callback in `ConfigurablePosesScanner`
   */
  useEffect(() => {
    if (!poseState) {
      return;
    }

    // If poses are different that means we captured -- so set to the new hint immediately
    // with no throttling
    if (previousPoseState != null && previousPoseState.currentPose !== poseState.currentPose) {
      setHintType(getHintType(poseState));
    } else {
      throttleSetHintType(getHintType(poseState));
    }

    setHasFace(poseState.hasFace);
  }, [poseState, previousPoseState]);

  useEffect(() => {
    if (delayUntilManualCaptureMs != null && delayUntilManualCaptureMs > 0) {
      setTimeout(() => {
        setManualButtonVisible(true);
      }, delayUntilManualCaptureMs);
    }
  }, [delayUntilManualCaptureMs]);

  const currentPose = poseState?.currentPose;
  const manualDisabled = currentPose == null || currentPose === 'done';
  const videoBlurActive = currentPose != null && currentPose !== SelfiePose.Center && hasFace;

  const hint = hintType != null ? TRANSLATIONS[hintType]({ localizations, t }) : undefined;

  // If we do not detect a face, pass in center pose and ProgressOverlay will show no animation
  // If we're done capturing, pass in undefined and ProgressOverlay will show a loading bar
  // Otherwise pass in the current pose to show the corresponding animation
  // TODO: refactor ProgressOverlay so that we don't have to do this hack to remove the animation
  const stepForAnimation = !hasFace
    ? SelfieStep.Center
    : currentPose === 'done'
      ? undefined
      : (currentPose ?? poseOrder[0]);

  return (
    <Container>
      <BaseHintPrompt
        styles={styles?.captureHintText}
        hintText={showLoadingSpinner ? t('selfie-scanner.hint.loading-camera') : hint}
      />
      {showLoadingSpinner && (
        <LoadingSpinnerWrapper backgroundColor={styles?.base?.backgroundColor}>
          <FullScreenLoadingSpinner />
        </LoadingSpinnerWrapper>
      )}
      <VideoWrapper style={videoHeight ? { maxHeight: videoHeight } : undefined}>
        <VideoBlurrer
          // TODO: [PER-23734] This is apparently causing issues on iOS 17.
          isActive={videoBlurActive && !isIOS17Phone()}
          backgroundColor={styles?.base?.backgroundColor}
        >
          {children}
        </VideoBlurrer>
        <OverlayPositioner>
          <ProgressOverlay
            assets={assets}
            showDelayedHint={true}
            isNarrowed={hasFace}
            profileSize={profileSize}
            currentStep={stepForAnimation}
            poseOrder={poseOrder}
            styles={styles}
            showLoadingCircle={false}
            enableMobilePendingExperienceForWeb={enableMobilePendingExperienceForWeb}
          />
        </OverlayPositioner>
      </VideoWrapper>
      {manualButtonVisible && currentPose !== 'done' && (
        <PositionableButtonContainer>
          <ButtonStyled
            id='configurable_poses_scanner__capture--manual'
            data-test='configurable_poses_scanner__capture--manual'
            onClick={handleManualCapture}
            disabled={manualDisabled}
            styles={styles?.buttons?.[ButtonType.Primary]}
          >
            {t('selfie-scanner.prompt.btn-capture-manual')}
          </ButtonStyled>
        </PositionableButtonContainer>
      )}
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  max-width: 100%;
  margin: 0 auto;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: 100%;
  /* Hide the "glow" from blur filter */
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  video {
    transform: scaleX(-1);
    flex: 0 1 auto;
  }
`;

const OverlayPositioner = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ButtonStyled = styled(Button)`
  margin-top: ${Spacing[4]};
  margin-bottom: ${Spacing[5]};
`;

const VIDEO_BLURRER_BORDER = 5;
const VideoBlurrer = styled('div')<{
  isActive: boolean;
  backgroundColor: StyleValueColorConfig | undefined;
}>`
  /* Without this, <video> will have some whitespace at bottom */
  line-height: 0;
  transition: filter 0.5s;
  filter: ${(props) => (props.isActive ? 'blur(5px)' : '')};
  /* NB: HACK to cover up the "glow" */
  border-width: ${VIDEO_BLURRER_BORDER}px;
  border-style: solid;
  border-color: ${({ isActive, backgroundColor, theme }) =>
    isActive ? `${colorCss(backgroundColor) ?? theme.colorModalBackground}` : 'transparent'};
`;

const LoadingSpinnerWrapper = styled.div<{ backgroundColor: StyleValueColorConfig | undefined }>`
  background-color: ${({ backgroundColor, theme }) =>
    `${colorCss(backgroundColor) ?? theme.colorModalBackground}`};
  position: absolute;
  width: 100%;
  padding-bottom: 100%;
  z-index: 1;
`;

const getHintType = (poseState: PoseState): HintType => {
  const invalidReason = poseState.invalidReasons?.[0];
  return invalidReason && poseState.currentPose !== 'done' ? invalidReason : poseState.currentPose;
};

const TRANSLATIONS: Record<
  HintType,
  (args: {
    localizations?: StepSelfieConfig['localizations']['capture-page'];
    t: TFunction;
  }) => string
> = {
  [SelfiePose.Center]: ({ localizations, t }) =>
    localizations?.['selfie-hint-center-face'] ?? t('selfie-scanner.hint.center'),
  [SelfiePose.Left]: ({ localizations, t }) =>
    localizations?.['selfie-hint-look-left'] ?? t('selfie-scanner.hint.left'),
  [SelfiePose.Right]: ({ localizations, t }) =>
    localizations?.['selfie-hint-look-right'] ?? t('selfie-scanner.hint.right'),
  [InvalidSelfieReason.MultipleFaces]: ({ localizations, t }) =>
    localizations?.['selfie-hint-multiple-faces'] ?? t('selfie-scanner.hint.multiple-faces'),
  [InvalidSelfieReason.NotCentered]: ({ localizations, t }) =>
    localizations?.['selfie-hint-pose-not-center'] ?? t('selfie-scanner.hint.not-centered'),
  [InvalidSelfieReason.TooFar]: ({ localizations, t }) =>
    localizations?.['selfie-hint-face-too-far'] ?? t('selfie-scanner.hint.move-closer'),
  [InvalidSelfieReason.TooClose]: ({ localizations, t }) =>
    localizations?.['selfie-hint-face-too-close'] ?? t('selfie-scanner.hint.move-away'),
  [InvalidSelfieReason.NoFace]: ({ localizations, t }) =>
    localizations?.['selfie-hint-center-face'] ?? t('selfie-scanner.hint.center'),
  done: ({ localizations, t }) =>
    localizations?.['selfie-hint-verifying'] ?? t('selfie-scanner.hint.verifying'),
};
